/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// React
import React from 'react';
// redux hooks
import { useFieldArray, Controller } from 'react-hook-form';
// BASE components
import BASESvgIcon from '../../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from '../../../../../../@BASE/utils/BASEUtils';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/system/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
// custom components
import NumberedHeading from '../../../../../shared-components/forms/NumberedHeading';
import ConceptCategorizingLureArray from './ConceptCategorizingLureArray';
import StatementMaterialsArray from './StatementMaterialsArray';
// data
import { ReviewData } from '../../data/Defaults';

export default function ConceptCategorizingArray({
  control,
  register,
  setValue,
  getValues,
  errors,
  inputDir,
  materialPages
}) {
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content.core'
  });

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0
          }
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{ marginTop: '22px' }} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="py-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="grow m-0 font-semibold">
                      Statement {index + 1}
                    </Typography>
                    {index !== 0 && (
                      <IconButton
                        className="ml-16 mr-8"
                        aria-label="move up"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index - 1);
                        }}
                      >
                        <BASESvgIcon size={20}>
                          heroicons-solid:arrow-circle-up
                        </BASESvgIcon>
                      </IconButton>
                    )}
                    {index !== fields.length - 1 && (
                      <IconButton
                        className="mx-8"
                        aria-label="move down"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index + 1);
                        }}
                      >
                        <BASESvgIcon size={20}>
                          heroicons-solid:arrow-circle-down
                        </BASESvgIcon>
                      </IconButton>
                    )}
                    <IconButton
                      className="ml-8 mr-16"
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      <BASESvgIcon size={20}>
                        heroicons-outline:trash
                      </BASESvgIcon>
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box className="p-0">
                      <NumberedHeading
                        number="1"
                        title="Materials"
                        instructions="Using the controls below, please add materials for the statement."
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.materials`}
                        render={({ field }) => (
                          <StatementMaterialsArray
                            nestIndex={index}
                            inputDir={inputDir}
                            materialPages={materialPages}
                            {...{ control, errors }}
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="2"
                        title="Category Type"
                        instructions="Select the category type for this question."
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.type_id`}
                        render={({ field }) => (
                          <FormControl fullWidth className="mt-16" size="small">
                            <InputLabel id="select-label">
                              Category Type
                            </InputLabel>
                            <Select
                              {...field}
                              labelId="select-type_id"
                              id="type_id"
                              label="Category Type"
                              classes={{
                                select: 'flex items-center space-x-12'
                              }}
                              defaultValue="default"
                            >
                              <MenuItem value={0} className="space-x-12">
                                Observation vs Inference
                              </MenuItem>
                              <MenuItem value={1} className="space-x-12">
                                Cause vs Effect
                              </MenuItem>
                              <MenuItem value={2} className="space-x-12">
                                Supporting Point vs. Counterpoint
                              </MenuItem>
                              <MenuItem value={3} className="space-x-12">
                                Valid Cause/Effect vs. Invalid Cause/Effect
                              </MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="3"
                        title="Statement(s)"
                        instructions="Enter the statement(s) in the target language."
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.prompt`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Text"
                              placeholder="Text"
                              error={!!errors.content?.core?.[index]?.prompt}
                              helperText={
                                errors.content?.core?.[index]?.prompt?.message
                              }
                              fullWidth
                              multiline
                              minRows={5}
                              maxRows={10}
                              required
                              dir={inputDir}
                              size="small"
                            />
                          </>
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="4"
                        title="Lures"
                        instructions="Using the controls below, please add 2 lures for the statement."
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.lures`}
                        render={({ field }) => (
                          <ConceptCategorizingLureArray
                            nestIndex={index}
                            inputDir={inputDir}
                            {...{ control, errors }}
                          />
                        )}
                      />

                      <Divider className="mt-16" />
                    </Box>
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 && (
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-up
                          </BASESvgIcon>
                        }
                        size="small"
                      >
                        Move Up
                      </Button>
                    )}
                    {index !== fields.length - 1 && (
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-down
                          </BASESvgIcon>
                        }
                        size="small"
                      >
                        Move Down
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={
                        <BASESvgIcon size={20}>
                          heroicons-outline:trash
                        </BASESvgIcon>
                      }
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() =>
            append({
              id: BASEUtils.generateGUID(),
              review: ReviewData,
              materials: [
                {
                  material_title_en: '',
                  material_title_tl: '',
                  source_url: '',
                  media_url: '',
                  media_material: false,
                  media_page_id: ''
                }
              ],
              type_id: 0,
              prompt: '',
              lures: [
                {
                  lure_text: '',
                  lure_feedback: '',
                  is_correct: false
                },
                {
                  lure_text: '',
                  lure_feedback: '',
                  is_correct: false
                }
              ]
            })
          }
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add statement
          </span>
        </Button>
      </Box>
    </>
  );
}
